/**
 * The left rail in sandbox mode: a real repository with nothing at stake.
 *
 * Why this exists. Until now the only real terminal in the product was inside
 * a graded level. The warm-up's try-prompts look like a shell and are not one
 * — the output is authored, so a card can answer instantly and stay correct
 * for ever (TryPrompt.tsx says so itself) — which means a learner who had
 * just read a chapter opener and wanted to type `git status` somewhere
 * harmless had nowhere to do it. They had to start a level, and starting a
 * level is the thing they were not ready for.
 *
 * So a sandbox is a level's own seeded workspace, opened with the grading
 * switched off: no objectives, no Check, no hints, no XP, no attempt
 * recorded, no clear. The repository is real, the commits are real, `git log`
 * shows a history somebody plausibly wrote, and none of it counts. Reset
 * rebuilds it, which is the point — the safest thing you can hand a beginner
 * is a machine they cannot permanently break.
 *
 * It deliberately reuses a level rather than shipping a workspace of its own.
 * Levels are baked into the guest image (docs/contracts.md, "Levels baked
 * into the image"), so a bespoke sandbox would mean a new image, a new
 * manifest and twenty minutes of CI for a scratch repository that already
 * exists fifty times over.
 *
 * What it shows instead of the ticket is the level's warm-up cards, reduced
 * to their names. Those already name every tool the chapter uses, with the
 * flags worth knowing, and they are written per level — so the sandbox needs
 * no authored content of its own and cannot drift from the course.
 */
import type { PublicMission } from '../../types';
import { LinkButton } from '../../ui';
import { useStrings } from '../../state/useLocale';
import { COURSE_HREF } from '../../lib/landing';

export interface SandboxRailProps {
  mission: PublicMission;
  /** The chapter this workspace belongs to, when its opener is written. */
  openerSlug: string | null;
  /** Where "play it properly" goes. */
  realHref: string;
}

export function SandboxRail({ mission, openerSlug, realHref }: SandboxRailProps) {
  const strings = useStrings();
  const s = strings.progress.sandbox;
  const cards = mission.tutorial?.cards ?? [];
  // `world` is a join key and stays English in the data, so it is translated
  // here, where it is drawn (docs/i18n.md §6.1). A world with no entry yet
  // draws its English name rather than nothing.
  const world = strings.home.worlds[mission.world]?.name ?? mission.world;

  return (
    <>
      <section className="block">
        <p className="label sandbox-label">{s.label(world)}</p>
        <h1 className="rail-title">{s.title}</h1>
        <p className="lede">{s.lede(mission.level)}</p>
        <p className="note">{s.note}</p>
      </section>

      {cards.length > 0 && (
        <section className="block">
          <h2 className="label">{s.thingsToTry}</h2>
          {/* The warm-up's own cards, reduced to the tool and the one-line
              reason. Not the worked examples — those are the warm-up's job and
              it is one button away in the bar above. A list here is a menu,
              not a lesson. */}
          <ul className="sandbox-tools">
            {cards.map((c) => (
              // The tool is a command and stays in Latin, left to right,
              // whatever the page direction is; the reason beside it is the
              // warm-up's own prose and arrives translated with the mission.
              <li key={c.tool}>
                <code dir="ltr">{c.tool}</code>
                <span>{c.title}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="block">
        <h2 className="label">{s.whenReady}</h2>
        <p className="note">{s.whenReadyNote}</p>
        <LinkButton variant="solid" wide href={realHref}>
          {s.playForReal(mission.level)}
        </LinkButton>
        {openerSlug && (
          <LinkButton variant="quiet" wide href={`?opener=${encodeURIComponent(openerSlug)}`}>
            {s.backToChapter}
          </LinkButton>
        )}
        <LinkButton variant="quiet" wide href={COURSE_HREF}>
          {s.levelList}
        </LinkButton>
      </section>
    </>
  );
}

export default SandboxRail;
